import { useEffect, useState } from "react";
import { api } from "@/lib/firebase/api-client";
import { intervalleVisible } from "@/lib/utils/intervalle-visible";
import { libelleHeure } from "@/lib/utils/heure";

/**
 * ===========================================================================
 *  LES MAGASINS SONT-ILS OUVERTS ?
 * ===========================================================================
 *
 *  Deux lectures cote a cote, parce qu'une seule mentirait :
 *
 *    OUVERT / FERME     ce que le magasin devrait faire selon ses horaires
 *    DERNIERE COMMANDE  ce qu'il fait vraiment
 *
 *  Le cas qu'on cherche est celui ou les deux se contredisent : « ouvert »
 *  sur le papier, mais plus aucune commande depuis une heure en plein
 *  service. C'est presque toujours un magasin coupe cote Glovo — une
 *  tablette eteinte, une pause oubliee — et l'horaire seul ne le montrerait
 *  jamais. Cette ligne-la passe en orange.
 * ===========================================================================
 */

/** Au-dela, un magasin ouvert qui ne recoit rien est suspect. */
const SILENCE_SUSPECT_MIN = 60;

type Magasin = {
  id: string;
  nom: string;
  actif: boolean;
  heureOuverture: number;
  heureFermeture: number;
  jourFerme: boolean;
  ouvertSelonHoraires: boolean;
  derniereCommandeA: string | null;
};

function depuis(iso: string | null): { minutes: number; texte: string } | null {
  if (!iso) return null;
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return { minutes, texte: "à l'instant" };
  if (minutes < 60) return { minutes, texte: `il y a ${minutes} min` };
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return { minutes, texte: `il y a ${h} h${m ? ` ${String(m).padStart(2, "0")}` : ""}` };
  return {
    minutes,
    texte: `le ${new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      timeZone: "Africa/Casablanca",
    })}`,
  };
}

export default function EtatMagasins() {
  const [magasins, setMagasins] = useState<Magasin[] | null>(null);
  // Force un nouveau rendu chaque minute : « il y a 12 min » doit vieillir
  // meme si rien ne change dans la base.
  const [, setTic] = useState(0);

  useEffect(() => {
    let annule = false;
    async function charger() {
      try {
        const r = await api.stores.etat();
        if (!annule) setMagasins(r.magasins || []);
      } catch {
        /* Le bloc est un confort : son absence ne casse pas la page. */
      }
    }
    void charger();
    // Onglet cache : plus d'appel au serveur ; retour : rafraichi aussitot.
    const arret = intervalleVisible(() => {
      void charger();
      setTic((t) => t + 1);
    }, 60000);
    return () => {
      annule = true;
      arret();
    };
  }, []);

  if (!magasins || magasins.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
        État des magasins
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {magasins.map((m) => {
          const d = depuis(m.derniereCommandeA);
          const silence =
            m.ouvertSelonHoraires && (!d || d.minutes >= SILENCE_SUSPECT_MIN);

          const couleur = silence
            ? "border-amber-300 bg-amber-50"
            : m.ouvertSelonHoraires
              ? "border-green-200 bg-green-50"
              : "border-gray-200 bg-gray-50";

          return (
            <div key={m.id} className={`rounded-xl border-2 px-4 py-3 ${couleur}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-bold text-gray-900 truncate">{m.nom}</p>
                <span
                  className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 py-1 ${
                    m.ouvertSelonHoraires
                      ? "bg-green-600 text-white"
                      : "bg-gray-300 text-gray-700"
                  }`}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      m.ouvertSelonHoraires ? "bg-white" : "bg-gray-500"
                    }`}
                  />
                  {m.ouvertSelonHoraires ? "OUVERT" : "FERMÉ"}
                </span>
              </div>

              <p className="text-xs text-gray-500 mt-1">
                {!m.actif
                  ? "magasin désactivé"
                  : m.jourFerme
                    ? "jour de fermeture"
                    : `horaires ${libelleHeure(m.heureOuverture)} – ${libelleHeure(m.heureFermeture)}`}
              </p>

              <p className="text-xs text-gray-700 mt-0.5">
                dernière commande :{" "}
                <span className="font-semibold">{d ? d.texte : "aucune"}</span>
              </p>

              {/* LA CONTRADICTION, ECRITE EN CLAIR. « Ouvert » et « rien
                  depuis une heure » ne peuvent pas etre vrais ensemble bien
                  longtemps : l'un des deux est faux, et c'est souvent le
                  premier. */}
              {silence && (
                <p className="text-xs font-bold text-amber-800 mt-1.5">
                  Ouvert selon ses horaires, mais aucune commande{" "}
                  {d ? `depuis ${d.texte.replace("il y a ", "")}` : "aujourd'hui"} —
                  vérifier sur Glovo.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
